import React, { useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useCreateImportUploadMutation } from '../../../../../api/schedulingApi';
import type { ScheduleImport } from '../../../../../types/scheduling';

const ACCEPT = '.xlsx,.xls,.csv';
const MAX_BYTES = 20 * 1024 * 1024;

interface ProvideStepProps {
  scheduleId: string;
  studioId: string;
  onReady: (importRow: ScheduleImport) => void;
}

/**
 * Step 1 — upload a structured spreadsheet (xlsx/csv). The backend parses it
 * synchronously and returns the import already "ready" with a canonical draft.
 * Photo/PDF (AI) import is a later addition; this MVP covers the free path.
 */
const ProvideStep: React.FC<ProvideStepProps> = ({
  scheduleId,
  studioId,
  onReady,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createUpload, { isLoading }] = useCreateImportUploadMutation();

  const handleFile = async (file: File) => {
    setError(null);
    const name = file.name.toLowerCase();
    if (!['.xlsx', '.xls', '.csv'].some(ext => name.endsWith(ext))) {
      setError('Please upload a .xlsx, .xls or .csv file.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('File exceeds the 20MB limit.');
      return;
    }
    try {
      const result = await createUpload({
        scheduleId,
        studioId,
        file,
      }).unwrap();
      if (result.status === 'failed') {
        setError(
          result.error?.message ||
            'We could not read that file. Check the columns and try again.'
        );
        return;
      }
      onReady(result);
    } catch (err: any) {
      setError(
        err?.data?.detail ||
          err?.data?.file ||
          'Upload failed. Please try again.'
      );
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload a spreadsheet of your schedule — one row per class session, with
        columns like class, day, start, end, room and teacher. We will read it
        into a review screen before anything is saved.
      </Typography>

      <Box
        onClick={() => inputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={e => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        sx={{
          border: theme =>
            `2px dashed ${dragging ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: 2,
          p: 5,
          textAlign: 'center',
          cursor: isLoading ? 'default' : 'pointer',
          bgcolor: dragging ? 'action.hover' : 'transparent',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
        {isLoading ? (
          <CircularProgress size={32} />
        ) : (
          <>
            <UploadFileIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="subtitle1">
              Drop a spreadsheet here, or click to choose
            </Typography>
            <Typography variant="caption" color="text.secondary">
              .xlsx, .xls or .csv · up to 20MB
            </Typography>
          </>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        Importing a photo or PDF of a printed schedule is coming soon.
      </Alert>
    </Box>
  );
};

export default ProvideStep;
